import { Badge, Card } from "react-bootstrap";
import { Check, CheckCircle, Plus } from "react-bootstrap-icons";
import { Currency } from "../../../../lib/components/Currency";
import { CollapsibleAgentPanel } from "../../CollapsibleAgentPanel";
import { SubmitButton, CancelButton } from "../../shared";
import { CommissionsSubTable } from "./CommissionsSubTable";
import { BonusesSubTable } from "./BonusesSubTable";
import { ChargebacksSubTable } from "./ChargebacksSubTable";
import { AdjustmentsSubTable } from "./AdjustmentsSubTable";
import {
  isAgentGroupFullyReviewed,
  isBonusSelectable,
  isContractSelectable,
} from "../utils/recordSelection";

export function AgentPanel({
  agentGroup,
  isCollapsed,
  onToggleCollapse,
  expandedCommissionRows,
  onToggleCommissionRow,
  onAdjust,
  onReject,
  onApprove,
  onRemove,
  onReopen,
  selectedKeys,
  onToggleSelect,
  onToggleSelectAll,
  isSelectionDisabled,
  areRecordActionsDisabled,
  isLocked,
  onAddAgentAdjustment,
  onEditAgentAdjustment,
  onDeleteAgentAdjustment,
  deletingAdjustmentIds,
  onApproveAgent,
  isApprovingAgent,
  approvingRecordKeys,
  removingRecordKeys,
}) {
  const entryCount =
    agentGroup.contracts.length +
    agentGroup.bonuses.length +
    agentGroup.chargebacks.length +
    agentGroup.adjustments.length;

  const approvableCount =
    agentGroup.contracts.filter(isContractSelectable).length +
    agentGroup.bonuses.filter(isBonusSelectable).length;

  const isFullyApproved = isAgentGroupFullyReviewed(agentGroup);

  return (
    <CollapsibleAgentPanel
      title={agentGroup.agentLabel}
      badge={<Badge className="entry-count-badge">{entryCount} records</Badge>}
      summary={
        <div className="d-flex align-items-center gap-3">
          <AgentNetSummary net={agentGroup.totals.net} />
          {isFullyApproved ? (
            <span className="run-status-chip run-status-chip--locked flex-shrink-0">
              <CheckCircle size={12} /> Reviewed
            </span>
          ) : (
            onApproveAgent &&
            approvableCount > 0 && (
              <SubmitButton
                text={`Approve all (${approvableCount})`}
                color="green"
                className="flex-shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  onApproveAgent(agentGroup);
                }}
                disabled={areRecordActionsDisabled}
                isSubmitting={isApprovingAgent}
                icon={<Check size={14} />}
              />
            )
          )}
        </div>
      }
      isCollapsed={isCollapsed}
      onToggleCollapse={onToggleCollapse}
      borderVariant="primary"
      backgroundVariant="body-bg"
      className={`agent-panel mb-4${isLocked ? " agent-panel--locked" : ""}`}
    >
      <Card.Body className="agent-panel-body">
        <div className="agent-section agent-section--commissions">
          <div className="agent-section-label">Commissions</div>
          <CommissionsSubTable
            contracts={agentGroup.contracts}
            agentKey={agentGroup.key}
            expandedRows={expandedCommissionRows}
            onToggleRow={onToggleCommissionRow}
            onAdjust={onAdjust}
            onReject={onReject}
            onApprove={onApprove}
            onRemove={onRemove}
            onReopen={onReopen}
            selectedKeys={selectedKeys}
            onToggleSelect={onToggleSelect}
            onToggleSelectAll={onToggleSelectAll}
            isSelectionDisabled={isSelectionDisabled}
            areRecordActionsDisabled={areRecordActionsDisabled}
            approvingRecordKeys={approvingRecordKeys}
            removingRecordKeys={removingRecordKeys}
          />
        </div>

        <div className="agent-section agent-section--bonuses">
          <div className="agent-section-label">Bonuses</div>
          <BonusesSubTable
            records={agentGroup.bonuses}
            agentKey={agentGroup.key}
            onAdjust={onAdjust}
            onReject={onReject}
            onApprove={onApprove}
            onRemove={onRemove}
            onReopen={onReopen}
            selectedKeys={selectedKeys}
            onToggleSelect={onToggleSelect}
            onToggleSelectAll={onToggleSelectAll}
            isSelectionDisabled={isSelectionDisabled}
            areRecordActionsDisabled={areRecordActionsDisabled}
            approvingRecordKeys={approvingRecordKeys}
            removingRecordKeys={removingRecordKeys}
          />
        </div>

        <div className="agent-section agent-section--chargebacks">
          <div className="agent-section-label">Chargebacks</div>
          <ChargebacksSubTable
            records={agentGroup.chargebacks}
            agentKey={agentGroup.key}
            onAdjust={onAdjust}
            onReject={onReject}
            onApprove={onApprove}
            onRemove={onRemove}
            onReopen={onReopen}
            selectedKeys={selectedKeys}
            onToggleSelect={onToggleSelect}
            onToggleSelectAll={onToggleSelectAll}
            isSelectionDisabled={isSelectionDisabled}
            areRecordActionsDisabled={areRecordActionsDisabled}
            approvingRecordKeys={approvingRecordKeys}
            removingRecordKeys={removingRecordKeys}
          />
        </div>

        <div className="agent-section agent-section--adjustments">
          <div className="agent-section-label agent-section-label--with-action">
            <span>Agent adjustments</span>
            {!isLocked && onAddAgentAdjustment && (
              <CancelButton
                text="Add adjustment"
                size="sm"
                onClick={() => onAddAgentAdjustment?.(agentGroup)}
                disabled={areRecordActionsDisabled}
                icon={<Plus size={12} />}
              />
            )}
          </div>
          <AdjustmentsSubTable
            adjustments={agentGroup.adjustments}
            isLocked={isLocked}
            onEdit={onEditAgentAdjustment}
            onDelete={onDeleteAgentAdjustment}
            deletingAdjustmentIds={deletingAdjustmentIds}
          />
        </div>
      </Card.Body>
    </CollapsibleAgentPanel>
  );
}

function AgentNetSummary({ net }) {
  const tone = net < 0 ? "agent-net--danger" : "";
  return (
    <div className={`agent-net ${tone}`.trim()}>
      <span className="agent-net-label">Net Payout</span>
      <span className="agent-net-value tnum">
        <Currency amount={net} />
      </span>
    </div>
  );
}
